/**
 * Componente: Laudo de Atendimento
 * 
 * Este componente exibe o laudo completo da última OS do equipamento.
 * Migrado incrementalmente de App.js
 */

import React, { useState, useCallback } from 'react';
import { getLastOSParts, getLastOSPartsChanged } from '../services/equipmentService';
import { formatDateToPTBR, formatNumber, calculateTimeDifference } from '../utils/formatters';
import { History } from 'lucide-react';
import ServiceHistoryModal from './ServiceHistoryModal';

const LaudoStep = ({
  lastOsData,
  selectedEquipment,
  onClose
}) => {
  const [lastOsParts, setLastOsParts] = useState([]);
  const [isLoadingLastOsParts, setIsLoadingLastOsParts] = useState(false);
  const [lastOsPartsError, setLastOsPartsError] = useState(null);
  const [showLastOsParts, setShowLastOsParts] = useState(false);
  const [lastOsPartsChanged, setLastOsPartsChanged] = useState([]);
  const [isLoadingLastOsPartsChanged, setIsLoadingLastOsPartsChanged] = useState(false);
  const [lastOsPartsChangedError, setLastOsPartsChangedError] = useState(null);
  const [showLastOsPartsChanged, setShowLastOsPartsChanged] = useState(false);
  const [showServiceHistoryModal, setShowServiceHistoryModal] = useState(false);

  // Não buscar automaticamente - apenas quando o botão for clicado

  const fetchLastOsParts = useCallback(async (osNumber) => {
    if (!osNumber) return;
    setIsLoadingLastOsParts(true);
    setLastOsPartsError(null);
    try {
      const result = await getLastOSParts(osNumber);
      if (result.success) {
        setLastOsParts(result.items || []);
      } else {
        setLastOsParts([]);
      }
    } catch (error) {
      console.error('Erro ao buscar peças pendentes da OS:', error);
      setLastOsPartsError('Erro ao carregar peças pendentes.');
      setLastOsParts([]);
    } finally {
      setIsLoadingLastOsParts(false);
    }
  }, []);

  const fetchLastOsPartsChanged = useCallback(async (osNumber) => {
    if (!osNumber) return;
    setIsLoadingLastOsPartsChanged(true);
    setLastOsPartsChangedError(null);
    try {
      const result = await getLastOSPartsChanged(osNumber);
      if (result.success) {
        setLastOsPartsChanged(result.items || []);
      } else {
        setLastOsPartsChanged([]);
      }
    } catch (error) {
      console.error('Erro ao buscar peças trocadas da OS:', error);
      setLastOsPartsChangedError('Erro ao carregar peças trocadas.');
      setLastOsPartsChanged([]);
    } finally {
      setIsLoadingLastOsPartsChanged(false);
    }
  }, []);

  if (!lastOsData) {
    return null;
  }

  return (
    <div style={{
      background: 'var(--modal-bg, #ffffff)',
      borderRadius: '12px',
      padding: '20px',
      border: '1px solid var(--border-subtle, #e8e8e8)',
      boxShadow: '0 2px 8px rgba(124, 77, 255, 0.08)',
      flex: '1 1 auto',
      minWidth: '400px'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px'
      }}>
        <h3 style={{
          margin: 0,
          fontSize: '14px',
          fontWeight: 600,
          fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
          color: 'var(--text-body, #2d3748)',
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }}>
          Laudo de Atendimento - OS #{lastOsData.codigo}
        </h3>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
          <button
            type="button"
            className="open-os-btn-secondary"
            onClick={() => setShowServiceHistoryModal(true)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px',
              padding: '6px 12px',
              fontSize: '11px',
              fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
              fontWeight: 500,
              color: 'var(--text-muted, #4a5568)',
              background: 'var(--modal-bg, #ffffff)',
              border: '1px solid var(--border-subtle, #e2e8f0)',
              borderRadius: '6px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              whiteSpace: 'nowrap'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--bg-surface-alt, #f8fafc)';
              e.currentTarget.style.borderColor = 'var(--border-subtle, #cbd5e0)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'var(--modal-bg, #ffffff)';
              e.currentTarget.style.borderColor = 'var(--border-subtle, #e2e8f0)';
            }}
          >
            <History size={13} />
            <span>Histórico de serviço</span>
          </button>
          {onClose && (
            <button
              className="open-os-btn-ghost"
              onClick={onClose}
              style={{
                background: 'transparent',
                border: '1px solid var(--border-subtle, #e2e8f0)',
                borderRadius: '6px',
                width: '28px',
                height: '28px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                fontSize: '16px',
                color: 'var(--text-muted, #718096)',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'var(--bg-surface-alt, #f7fafc)';
                e.currentTarget.style.borderColor = 'var(--border-subtle, #cbd5e0)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.borderColor = 'var(--border-subtle, #e2e8f0)';
              }}
            >
              ×
            </button>
          )}
        </div>
      </div>

      {/* TÉCNICO */}
      {lastOsData.tecnico && (
        <div style={{ marginBottom: '20px' }}>
          <label style={{
            display: 'block',
            marginBottom: '4px',
            fontSize: '11px',
            fontWeight: 500,
            fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
            color: 'var(--text-muted, #718096)',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            TÉCNICO
          </label>
          <div style={{
            fontSize: '13px',
            fontWeight: 500,
            fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
            color: 'var(--text-body, #2d3748)'
          }}>
            {lastOsData.tecnico}
          </div>
        </div>
      )}

      {/* DATA DO ATENDIMENTO */}
      {lastOsData.dataFim && (
        <div style={{ marginBottom: '20px' }}>
          <label style={{
            display: 'block',
            marginBottom: '4px',
            fontSize: '11px',
            fontWeight: 500,
            fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
            color: 'var(--text-muted, #718096)',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            DATA DO ATENDIMENTO
          </label>
          <div style={{
            fontSize: '13px',
            fontWeight: 500,
            fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
            color: 'var(--text-body, #2d3748)'
          }}>
            {formatDateToPTBR(lastOsData.dataFim)}
            {lastOsData.horaIni && (
              <span style={{ marginLeft: '12px' }}>
                {lastOsData.horaIni.substring(0, 5)}
              </span>
            )}
            {lastOsData.horaFim && (
              <span style={{ marginLeft: '8px' }}>
                {lastOsData.horaFim.substring(0, 5)}
              </span>
            )}
            {lastOsData.horaIni && lastOsData.horaFim && (
              <span style={{ 
                marginLeft: '12px',
                fontSize: '12px',
                fontWeight: 400,
                color: 'var(--text-muted, #718096)'
              }}>
                tempo de atendimento: {calculateTimeDifference(lastOsData.horaIni, lastOsData.horaFim)}
              </span>
            )}
          </div>
        </div>
      )}

      {/* CONDIÇÃO DE INTERVENÇÃO */}
      {lastOsData.condicao && (
        <div style={{ marginBottom: '12px' }}>
          <label style={{
            display: 'block',
            marginBottom: '4px',
            fontSize: '11px',
            fontWeight: 500,
            fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
            color: 'var(--text-muted, #718096)',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            CONDIÇÃO DE INTERVENÇÃO
          </label>
          <div style={{
            fontSize: '13px',
            fontWeight: 500,
            fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
            color: 'var(--text-body, #2d3748)',
            marginBottom: '8px'
          }}>
            {lastOsData.condicao}
          </div>
          {lastOsData.obsTecnico && (
            <div style={{
              fontSize: '12px',
              fontWeight: 400,
              fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
              color: 'var(--text-muted, #4a5568)',
              lineHeight: '1.6',
              padding: '12px',
              background: 'var(--bg-surface-alt, #f7fafc)',
              borderRadius: '8px',
              border: '1px solid var(--border-subtle, #e2e8f0)'
            }}>
              {lastOsData.obsTecnico}
            </div>
          )}
        </div>
      )}

      {/* CONTADORES */}
      {(lastOsData.contador != null || lastOsData.contadorC != null || lastOsData.contadorDG != null || lastOsData.contadorGF != null || lastOsData.contadorGFC != null) && (
        <div style={{ marginBottom: '12px' }}>
          <label style={{
            display: 'block',
            marginBottom: '8px',
            fontSize: '11px',
            fontWeight: 500,
            fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
            color: 'var(--text-muted, #718096)',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            CONTADORES
          </label>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            border: '1px solid var(--border-subtle, #e2e8f0)'
          }}>
            <tbody>
              <tr>
                <td style={{
                  border: '1px solid var(--border-subtle, #e2e8f0)',
                  padding: '8px',
                  textAlign: 'center',
                  verticalAlign: 'top'
                }}>
                  <div style={{
                    fontSize: '11px',
                    fontWeight: 500,
                    fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                    color: 'var(--text-muted, #718096)',
                    marginBottom: '4px'
                  }}>
                    Contador P&B
                  </div>
                  <div style={{
                    fontSize: '13px',
                    fontWeight: 600,
                    fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                    color: 'var(--text-body, #2d3748)'
                  }}>
                    {lastOsData.contador != null ? formatNumber(lastOsData.contador) : '-'}
                  </div>
                </td>
                <td style={{
                  border: '1px solid var(--border-subtle, #e2e8f0)',
                  padding: '8px',
                  textAlign: 'center',
                  verticalAlign: 'top'
                }}>
                  <div style={{
                    fontSize: '11px',
                    fontWeight: 500,
                    fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                    color: 'var(--text-muted, #718096)',
                    marginBottom: '4px'
                  }}>
                    Contador Color
                  </div>
                  <div style={{
                    fontSize: '13px',
                    fontWeight: 600,
                    fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                    color: 'var(--text-body, #2d3748)'
                  }}>
                    {lastOsData.contadorC != null ? formatNumber(lastOsData.contadorC) : '-'}
                  </div>
                </td>
                <td style={{
                  border: '1px solid var(--border-subtle, #e2e8f0)',
                  padding: '8px',
                  textAlign: 'center',
                  verticalAlign: 'top'
                }}>
                  <div style={{
                    fontSize: '11px',
                    fontWeight: 500,
                    fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                    color: 'var(--text-muted, #718096)',
                    marginBottom: '4px'
                  }}>
                    Contador DG
                  </div>
                  <div style={{
                    fontSize: '13px',
                    fontWeight: 600,
                    fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                    color: 'var(--text-body, #2d3748)'
                  }}>
                    {lastOsData.contadorDG != null ? formatNumber(lastOsData.contadorDG) : '-'}
                  </div>
                </td>
                <td style={{
                  border: '1px solid var(--border-subtle, #e2e8f0)',
                  padding: '8px',
                  textAlign: 'center',
                  verticalAlign: 'top'
                }}>
                  <div style={{
                    fontSize: '11px',
                    fontWeight: 500,
                    fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                    color: 'var(--text-muted, #718096)',
                    marginBottom: '4px'
                  }}>
                    Contador A3 P&B
                  </div>
                  <div style={{
                    fontSize: '13px',
                    fontWeight: 600,
                    fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                    color: 'var(--text-body, #2d3748)'
                  }}>
                    {lastOsData.contadorGF != null ? formatNumber(lastOsData.contadorGF) : '-'}
                  </div>
                </td>
                <td style={{
                  border: '1px solid var(--border-subtle, #e2e8f0)',
                  padding: '8px',
                  textAlign: 'center',
                  verticalAlign: 'top'
                }}>
                  <div style={{
                    fontSize: '11px',
                    fontWeight: 500,
                    fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                    color: 'var(--text-muted, #718096)',
                    marginBottom: '4px'
                  }}>
                    Contador A3 Color
                  </div>
                  <div style={{
                    fontSize: '13px',
                    fontWeight: 600,
                    fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                    color: 'var(--text-body, #2d3748)'
                  }}>
                    {lastOsData.contadorGFC != null ? formatNumber(lastOsData.contadorGFC) : '-'}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* PEÇAS PENDENTES E TROCADAS */}
      {(lastOsData.pecaPendente === 1 || lastOsData.pecaTrocada === 1) && (
        <div style={{ marginBottom: '12px' }}>
          <label style={{
            display: 'block',
            marginBottom: '8px',
            fontSize: '11px',
            fontWeight: 500,
            fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
            color: 'var(--text-muted, #718096)',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            PEÇAS
          </label>
          
          {/* Peças Pendentes */}
          {lastOsData.pecaPendente === 1 && (
            <div style={{ marginBottom: '12px' }}>
              <button
                onClick={async () => {
                  if (showLastOsParts) {
                    setShowLastOsParts(false);
                    return;
                  }
                  setShowLastOsPartsChanged(false);
                  if (!lastOsParts || lastOsParts.length === 0) {
                    await fetchLastOsParts(lastOsData.codigo);
                  }
                  setShowLastOsParts(true);
                }}
                style={{
                  fontSize: '9px',
                  fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                  fontWeight: 500,
                  color: '#7c4dff',
                  background: 'transparent',
                  border: '1px solid #7c4dff',
                  borderRadius: '4px',
                  padding: '6px 11px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  marginBottom: '8px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#7c4dff';
                  e.target.style.color = '#ffffff';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'transparent';
                  e.target.style.color = '#7c4dff';
                }}
              >
                {showLastOsParts ? 'Ocultar peças pendentes' : 'Visualizar peças pendentes'}
              </button>
              {showLastOsParts && (
                <div style={{ marginTop: '8px' }}>
                  {isLoadingLastOsParts && (
                    <div style={{ fontSize: '11px', fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif", color: 'var(--text-muted, #718096)' }}>
                      Carregando peças pendentes...
                    </div>
                  )}
                  {!isLoadingLastOsParts && lastOsPartsError && (
                    <div style={{ fontSize: '11px', fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif", color: '#c53030' }}>
                      {lastOsPartsError}
                    </div>
                  )}
                  {!isLoadingLastOsParts && !lastOsPartsError && lastOsParts && lastOsParts.length === 0 && (
                    <div style={{ fontSize: '11px', fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif", color: 'var(--text-muted, #718096)' }}>
                      Nenhuma peça pendente encontrada.
                    </div>
                  )}
                  {!isLoadingLastOsParts && !lastOsPartsError && lastOsParts && lastOsParts.length > 0 && (
                    <table style={{
                      width: 'auto',
                      borderCollapse: 'collapse',
                      marginTop: '4px',
                      border: '1px solid var(--border-subtle, #e2e8f0)'
                    }}>
                        <thead>
                          <tr>
                            <th style={{
                              border: '1px solid var(--border-subtle, #e2e8f0)',
                              padding: '4px 6px',
                              fontSize: '10px',
                              fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                              fontWeight: 600,
                              color: 'var(--text-muted, #4a5568)',
                              textAlign: 'left',
                              whiteSpace: 'nowrap'
                            }}>
                              Código
                            </th>
                            <th style={{
                              border: '1px solid var(--border-subtle, #e2e8f0)',
                              padding: '4px 6px',
                              fontSize: '10px',
                              fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                              fontWeight: 600,
                              color: 'var(--text-muted, #4a5568)',
                              textAlign: 'left'
                            }}>
                              Produto
                            </th>
                            <th style={{
                              border: '1px solid var(--border-subtle, #e2e8f0)',
                              padding: '4px 6px',
                              fontSize: '10px',
                              fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                              fontWeight: 600,
                              color: 'var(--text-muted, #4a5568)',
                              textAlign: 'left'
                            }}>
                              Referência
                            </th>
                            <th style={{
                              border: '1px solid var(--border-subtle, #e2e8f0)',
                              padding: '4px 6px',
                              fontSize: '10px',
                              fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                              fontWeight: 600,
                              color: 'var(--text-muted, #4a5568)',
                              textAlign: 'center',
                              whiteSpace: 'nowrap'
                            }}>
                              Qtde
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {lastOsParts.map((item, index) => (
                            <tr key={`${item.codigo}-${index}`}>
                              <td style={{
                                border: '1px solid var(--border-subtle, #e2e8f0)',
                                padding: '3px 6px',
                                fontSize: '10px',
                                fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                                color: 'var(--text-body, #2d3748)',
                                whiteSpace: 'nowrap'
                              }}>
                                {item.codigo}
                              </td>
                              <td style={{
                                border: '1px solid var(--border-subtle, #e2e8f0)',
                                padding: '3px 6px',
                                fontSize: '10px',
                                fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                                color: 'var(--text-body, #2d3748)'
                              }}>
                                {item.nome
                                  ? (item.nome.length > 62
                                      ? item.nome.slice(0, 62) + '…'
                                      : item.nome)
                                  : ''}
                              </td>
                              <td style={{
                                border: '1px solid var(--border-subtle, #e2e8f0)',
                                padding: '3px 6px',
                                fontSize: '10px',
                                fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                                color: 'var(--text-muted, #4a5568)'
                              }}>
                                {item.referencia
                                  ? (item.referencia.length > 21
                                      ? item.referencia.slice(0, 21) + '…'
                                      : item.referencia)
                                  : ''}
                              </td>
                              <td style={{
                                border: '1px solid var(--border-subtle, #e2e8f0)',
                                padding: '3px 6px',
                                fontSize: '10px',
                                fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                                color: 'var(--text-body, #2d3748)',
                                textAlign: 'center',
                                whiteSpace: 'nowrap'
                              }}>
                                {item.quantidade}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Peças Trocadas */}
          {lastOsData.pecaTrocada === 1 && (
            <div>
              <button
                onClick={async () => {
                  if (showLastOsPartsChanged) {
                    setShowLastOsPartsChanged(false);
                    return;
                  }
                  setShowLastOsParts(false);
                  if (!lastOsPartsChanged || lastOsPartsChanged.length === 0) {
                    await fetchLastOsPartsChanged(lastOsData.codigo);
                  }
                  setShowLastOsPartsChanged(true);
                }}
                style={{
                  fontSize: '9px',
                  fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                  fontWeight: 500,
                  color: '#7c4dff',
                  background: 'transparent',
                  border: '1px solid #7c4dff',
                  borderRadius: '4px',
                  padding: '6px 11px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  marginBottom: '8px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#7c4dff';
                  e.target.style.color = '#ffffff';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'transparent';
                  e.target.style.color = '#7c4dff';
                }}
              >
                {showLastOsPartsChanged ? 'Ocultar peças trocadas' : 'Visualizar peças trocadas'}
              </button>
              {showLastOsPartsChanged && (
                <div style={{ marginTop: '8px' }}>
                  {isLoadingLastOsPartsChanged && (
                    <div style={{ fontSize: '11px', fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif", color: 'var(--text-muted, #718096)' }}>
                      Carregando peças trocadas...
                    </div>
                  )}
                  {!isLoadingLastOsPartsChanged && lastOsPartsChangedError && (
                    <div style={{ fontSize: '11px', fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif", color: '#c53030' }}>
                      {lastOsPartsChangedError}
                    </div>
                  )}
                  {!isLoadingLastOsPartsChanged && !lastOsPartsChangedError && lastOsPartsChanged && lastOsPartsChanged.length === 0 && (
                    <div style={{ fontSize: '11px', fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif", color: 'var(--text-muted, #718096)' }}>
                      Nenhuma peça trocada encontrada.
                    </div>
                  )}
                  {!isLoadingLastOsPartsChanged && !lastOsPartsChangedError && lastOsPartsChanged && lastOsPartsChanged.length > 0 && (
                    <table style={{
                      width: 'auto',
                      borderCollapse: 'collapse',
                      marginTop: '4px',
                      border: '1px solid var(--border-subtle, #e2e8f0)'
                    }}>
                        <thead>
                          <tr>
                            <th style={{
                              border: '1px solid var(--border-subtle, #e2e8f0)',
                              padding: '4px 6px',
                              fontSize: '10px',
                              fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                              fontWeight: 600,
                              color: 'var(--text-muted, #4a5568)',
                              textAlign: 'left',
                              whiteSpace: 'nowrap'
                            }}>
                              Código
                            </th>
                            <th style={{
                              border: '1px solid var(--border-subtle, #e2e8f0)',
                              padding: '4px 6px',
                              fontSize: '10px',
                              fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                              fontWeight: 600,
                              color: 'var(--text-muted, #4a5568)',
                              textAlign: 'left'
                            }}>
                              Produto
                            </th>
                            <th style={{
                              border: '1px solid var(--border-subtle, #e2e8f0)',
                              padding: '4px 6px',
                              fontSize: '10px',
                              fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                              fontWeight: 600,
                              color: 'var(--text-muted, #4a5568)',
                              textAlign: 'left'
                            }}>
                              Referência
                            </th>
                            <th style={{
                              border: '1px solid var(--border-subtle, #e2e8f0)',
                              padding: '4px 6px',
                              fontSize: '10px',
                              fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                              fontWeight: 600,
                              color: 'var(--text-muted, #4a5568)',
                              textAlign: 'center',
                              whiteSpace: 'nowrap'
                            }}>
                              Qtde
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {lastOsPartsChanged.map((item, index) => (
                            <tr key={`${item.codigo}-${index}`}>
                              <td style={{
                                border: '1px solid var(--border-subtle, #e2e8f0)',
                                padding: '3px 6px',
                                fontSize: '10px',
                                fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                                color: 'var(--text-body, #2d3748)',
                                whiteSpace: 'nowrap'
                              }}>
                                {item.codigo}
                              </td>
                              <td style={{
                                border: '1px solid var(--border-subtle, #e2e8f0)',
                                padding: '3px 6px',
                                fontSize: '10px',
                                fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                                color: 'var(--text-body, #2d3748)'
                              }}>
                                {item.nome
                                  ? (item.nome.length > 62
                                      ? item.nome.slice(0, 62) + '…'
                                      : item.nome)
                                  : ''}
                              </td>
                              <td style={{
                                border: '1px solid var(--border-subtle, #e2e8f0)',
                                padding: '3px 6px',
                                fontSize: '10px',
                                fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                                color: 'var(--text-muted, #4a5568)'
                              }}>
                                {item.referencia
                                  ? (item.referencia.length > 21
                                      ? item.referencia.slice(0, 21) + '…'
                                      : item.referencia)
                                  : ''}
                              </td>
                              <td style={{
                                border: '1px solid var(--border-subtle, #e2e8f0)',
                                padding: '3px 6px',
                                fontSize: '10px',
                                fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                                color: 'var(--text-body, #2d3748)',
                                textAlign: 'center',
                                whiteSpace: 'nowrap'
                              }}>
                                {item.quantidade}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <ServiceHistoryModal
        open={showServiceHistoryModal}
        onClose={() => setShowServiceHistoryModal(false)}
        serie={selectedEquipment?.serie}
        patrimonio={selectedEquipment?.patrimonio}
        modelo={selectedEquipment?.produto}
      />
    </div>
  );
};

export default LaudoStep;

